import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { NOTIFICATION_TYPE } from 'src/modules/Notification';

import Button from 'src/components/Button';

import theme from 'src/styles/components/AddOrderPosition.module.scss';

const AddOrderPosition = ({
    configurablePosition,
    context,
    increaseQuantity,
    decreaseQuantity,
    addOrderPosition,
    isOrderPositionAdded,
    dismissFirstMessage,
    clearSelectedMenuPosition,
    addOrderPositionError,
    pushNotification,
    clearAddOrderPositionErrorMessage,
    shouldCreateNewOrder,
    isAddPositionPossible
}) => {
    const intl = useIntl();

    useEffect(() => () => {
        clearAddOrderPositionErrorMessage();
    }, []);

    useEffect(() => {
        if (isOrderPositionAdded) {
            dismissFirstMessage();
            clearSelectedMenuPosition();
        }
    }, [isOrderPositionAdded]);

    useEffect(() => {
        if (addOrderPositionError) {
            pushNotification({
                type: NOTIFICATION_TYPE.WARNING,
                message: intl.formatMessage({ id: `Error.${addOrderPositionError}` }),
                isDismissButtonEnabled: true,
                timeout: 3000
            });

            clearAddOrderPositionErrorMessage();
        }
    }, [addOrderPositionError]);

    const handleAddPosition = useCallback(async () => {
        addOrderPosition(configurablePosition, context, shouldCreateNewOrder);
    }, [configurablePosition, shouldCreateNewOrder]);

    return (
        <div className={theme.container}>
            <div className={theme.quantityContainer}>
                <Button
                  floating
                  mini
                  inverse
                  icon="icon-minus"
                  onClick={decreaseQuantity}
                  iconSmall
                />
                <div className={theme.quantity}>
                    {configurablePosition && configurablePosition.quantity}
                </div>
                <Button
                  floating
                  mini
                  inverse
                  icon="icon-plus"
                  onClick={increaseQuantity}
                  iconSmall
                />
            </div>
            {isAddPositionPossible && (
                <Button
                  className={theme.orderButton}
                  raised
                  accent
                  onClick={handleAddPosition}
                >
                    {intl.formatMessage({ id: 'App.add' })}:
                    <span className={theme.price}>
                        {configurablePosition
                            && intl.formatNumber(
                                (configurablePosition.calculatedPrice || configurablePosition.price) * configurablePosition.quantity,
                            { style: 'currency', currency: 'PLN', minimumFractionDigits: 2 }
                        )}
                    </span>
                </Button>
            )}
            {!isAddPositionPossible && (
                <Button
                  className={theme.summaryButton}
                  flat
                  disabled
                  primary
                >
                    {intl.formatMessage({ id: 'App.price' })}:
                    <span className={theme.price}>
                        {configurablePosition
                            && intl.formatNumber(
                                (configurablePosition.calculatedPrice || configurablePosition.price) * configurablePosition.quantity,
                            { style: 'currency', currency: 'PLN', minimumFractionDigits: 2 }
                        )}
                    </span>
                </Button>
            )}
        </div>
    );
};

AddOrderPosition.propTypes = {
    configurablePosition: PropTypes.shape({
        id: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        calculatedPrice: PropTypes.number,
        quantity: PropTypes.number
    }),
    context: PropTypes.shape({
        restaurantId: PropTypes.string,
        tableId: PropTypes.string
    }),
    increaseQuantity: PropTypes.func.isRequired,
    decreaseQuantity: PropTypes.func.isRequired,
    addOrderPosition: PropTypes.func.isRequired,
    isOrderPositionAdded: PropTypes.bool.isRequired,
    dismissFirstMessage: PropTypes.func.isRequired,
    clearSelectedMenuPosition: PropTypes.func.isRequired,
    addOrderPositionError: PropTypes.string,
    pushNotification: PropTypes.func.isRequired,
    clearAddOrderPositionErrorMessage: PropTypes.func.isRequired,
    shouldCreateNewOrder: PropTypes.bool.isRequired,
    isAddPositionPossible: PropTypes.bool
};

export default AddOrderPosition;
